import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ArrowDown, Clock, Users, Award, Star, Code, ExternalLink } from "lucide-react";

const Index = () => {
  const certifications = [
    {
      id: "csce",
      name: "CSCE",
      fullName: "Converged Security & Cybersecurity Expert",
      level: "Foundational Certification",
      description: "Essential cybersecurity knowledge and practical skills for integrated physical and digital security environments.",
      prerequisites: "No prior cybersecurity experience required",
      timeEstimate: "Program duration + final assessment",
      cost: "€250",
      validity: "3 years",
      badge: "./images/csce.png",
      color: "from-pink-500 to-pink-600"
    },
    {
      id: "csp",
      name: "CSP",
      fullName: "Converged Security Professional",
      level: "Advanced Certification",
      description: "Strategic insight and operational leadership for cross-functional teams and enterprise-wide security initiatives.",
      prerequisites: "CSCE certification required",
      timeEstimate: "On-site small group format + group presentation",
      cost: "€750",
      validity: "3 years",
      badge: "./images/csp.png",
      color: "from-blue-600 to-blue-700"
    },
    {
      id: "csl",
      name: "CSL",
      fullName: "Converged Security Leader",
      level: "Leadership Credential",
      description: "Highest credential recognizing advanced expertise, strategic insight, and operational readiness for enterprise leadership.",
      prerequisites: "Both CSCE and CSP certifications",
      timeEstimate: "Awarded automatically upon completion of prerequisites",
      cost: "No additional fee",
      validity: "3 years",
      badge: "./images/leader.png",
      color: "from-yellow-500 to-yellow-600"
    },
    {
      id: "ccsp",
      name: "C-CSP",
      fullName: "Chartered Converged Security Practitioner",
      level: "Premier / Chartered Designation",
      description: "Elite-level leadership, strategic integration and operational excellence in implementing enterprise-wide converged security frameworks.",
      prerequisites: "CSCE + CSP + CSL + 5 years leadership experience + framework implementation",
      timeEstimate: "Application process + implementation verification",
      cost: "€500 (€50 application + €450 final)",
      validity: "3 years (Chartered Award)",
      badge: null,
      color: "from-purple-600 to-purple-700"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-800 to-blue-900 text-white py-12">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            CSI Certification Pathway
          </h1>
          <p className="text-xl text-blue-100 max-w-3xl mx-auto mb-6">
            Progressive structure designed to develop expertise from foundational knowledge to chartered practitioner level
          </p>
          <div className="flex justify-center gap-4">
            <Button 
              variant="outline" 
              className="bg-white/10 text-white border-white/20 hover:bg-white/20"
              onClick={() => window.open('/#/embed-docs', '_blank')}
            >
              <Code className="w-4 h-4 mr-2" />
              Get Embed Code
            </Button>
            <Button 
              variant="outline" 
              className="bg-white/10 text-white border-white/20 hover:bg-white/20"
              onClick={() => window.open('/#/embed', '_blank')}
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              Preview Embed
            </Button>
          </div>
        </div>
      </div>

      {/* Certification Pathway */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="relative">
          {certifications.map((cert, index) => (
            <div key={cert.id} className="relative mb-8">
              {/* Certification Card */}
              <Card className="relative overflow-hidden shadow-lg hover:shadow-xl transition-shadow duration-300">
                <div className={`absolute top-0 left-0 right-0 h-2 bg-gradient-to-r ${cert.color}`}></div>
                
                <CardHeader className="pb-4">
                  <div className="flex items-start gap-6">
                    {/* Badge Image */}
                    <div className="flex-shrink-0 cursor-pointer" onClick={() => {
                      if (cert.id === 'csce') window.location.href = '/#/csce';
                      else if (cert.id === 'csp') window.location.href = '/#/csp';
                      else if (cert.id === 'csl') window.location.href = '/#/csl';
                      else if (cert.id === 'ccsp') window.location.href = '/#/ccsp';
                    }}>
                      {cert.badge ? (
                        <img 
                          src={cert.badge} 
                          alt={`${cert.name} Badge`}
                          className={`w-20 h-20 object-contain hover:scale-105 transition-transform`}
                          title={`Click to learn more about ${cert.name}`}
                        />
                      ) : (
                        <div className={`w-20 h-20 rounded-full bg-gradient-to-br ${cert.color} flex items-center justify-center hover:scale-105 transition-transform cursor-pointer`}>
                          <Award className="w-10 h-10 text-white" />
                        </div>
                      )}
                    </div>
                    
                    {/* Certification Info */}
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <CardTitle className="text-2xl font-bold text-slate-800">
                          {cert.name}
                        </CardTitle>
                        <Badge variant="secondary" className="text-xs">
                          {cert.level}
                        </Badge>
                        {cert.id === 'csce' && (
                          <Button 
                            size="sm" 
                            variant="outline"
                            className="ml-auto text-xs"
                            onClick={() => window.location.href = '/#/csce'}
                          >
                            Learn More
                          </Button>
                        )}
                        {cert.id === 'csp' && (
                          <Button 
                            size="sm" 
                            variant="outline"
                            className="ml-auto text-xs"
                            onClick={() => window.location.href = '/#/csp'}
                          >
                            Learn More
                          </Button>
                        )}
                        {cert.id === 'csl' && (
                          <Button 
                            size="sm" 
                            variant="outline"
                            className="ml-auto text-xs"
                            onClick={() => window.location.href = '/#/csl'}
                          >
                            Learn More
                          </Button>
                        )}
                        {cert.id === 'ccsp' && (
                          <Button 
                            size="sm" 
                            variant="outline"
                            className="ml-auto text-xs"
                            onClick={() => window.location.href = '/#/ccsp'}
                          >
                            Learn More
                          </Button>
                        )}
                      </div>
                      <CardDescription className="text-lg font-medium text-slate-600 mb-2">
                        {cert.fullName}
                      </CardDescription>
                      <p className="text-slate-600 leading-relaxed">
                        {cert.description}
                      </p>
                    </div>
                  </div>
                </CardHeader>

                <CardContent className="pt-0">
                  <div className="grid md:grid-cols-2 gap-4">
                    {/* Prerequisites */}
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-sm font-medium text-slate-700">
                        <Users className="w-4 h-4" />
                        Prerequisites
                      </div>
                      <p className="text-sm text-slate-600 pl-6">
                        {cert.prerequisites}
                      </p>
                    </div>

                    {/* Time Estimate */}
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-sm font-medium text-slate-700">
                        <Clock className="w-4 h-4" />
                        Format & Duration
                      </div>
                      <p className="text-sm text-slate-600 pl-6">
                        {cert.timeEstimate}
                      </p>
                    </div>

                    {/* Cost */}
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-sm font-medium text-slate-700">
                        <Star className="w-4 h-4" />
                        Cost
                      </div>
                      <p className="text-sm text-slate-600 pl-6 font-medium">
                        {cert.cost}
                      </p>
                    </div>

                    {/* Validity */}
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-sm font-medium text-slate-700">
                        <Award className="w-4 h-4" />
                        Validity
                      </div>
                      <p className="text-sm text-slate-600 pl-6">
                        {cert.validity}
                      </p>
                    </div>
                  </div>
                  
                  {/* Learn More Button */}
                  <div className="mt-6 pt-4 border-t border-slate-200">
                    <Button 
                      className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
                      onClick={() => {
                        const paths = {
                          'CSCE': '/csce',
                          'CSP': '/csp', 
                          'CSL': '/csl',
                          'C-CSP': '/ccsp'
                        };
                        window.location.hash = `#${paths[cert.name] || '/'}`;
                      }}
                    >
                      <BookOpen className="w-4 h-4 mr-2" />
                      Learn More About {cert.name}
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Arrow Connector */}
              {index < certifications.length - 1 && (
                <div className="flex justify-center my-6">
                  <div className="flex flex-col items-center">
                    <ArrowDown className="w-8 h-8 text-slate-400 animate-bounce" />
                    <div className="w-px h-4 bg-slate-300"></div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Footer Information */}
        <div className="mt-12 p-6 bg-gradient-to-r from-slate-800 to-blue-900 rounded-lg text-white">
          <h3 className="text-xl font-bold mb-4">Key Information</h3>
          <div className="grid md:grid-cols-2 gap-4 text-sm">
            <div>
              <h4 className="font-semibold mb-2">Certification Maintenance</h4>
              <p className="text-blue-100">40 hours of continuing education required during 3-year validity period</p>
            </div>
            <div>
              <h4 className="font-semibold mb-2">Instructor</h4>
              <p className="text-blue-100">Led by Dr. Vladimir Bunic, PhD in Cybersecurity</p>
            </div>
            <div>
              <h4 className="font-semibold mb-2">Format</h4>
              <p className="text-blue-100">Digital certificates and badges, verifiable and shareable</p>
            </div>
            <div>
              <h4 className="font-semibold mb-2">Compliance</h4>
              <p className="text-blue-100">Adheres to EU GDPR, Cybersecurity Act, and NIS Directive</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Index;